import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

type UseSearchParams = () => [params: URLSearchParams, search: string];

export const useGetSearchParams: UseSearchParams = () => {
    const { search } = useLocation();
    const params = useMemo(() => new URLSearchParams(search), [search]);

    return [params, search];
};

type UseSearchParamReturn<T extends string = string> = [paramValue: T | null, search: string];

export const useGetSearchParam = <T extends string = string>(
    param: string,
): UseSearchParamReturn<T> => {
    const [params, search] = useGetSearchParams();

    const paramValue = (params.get(param) ?? null) as T | null;

    return [paramValue, search];
};

const useCreateSearchParam = (param: string) => {
    const location = useLocation();

    return useCallback(
        (paramValue?: string | null) => {
            const searchParams = new URLSearchParams(location.search);
            if (paramValue) {
                searchParams.set(param, paramValue);
            } else {
                searchParams.delete(param);
            }
            return searchParams;
        },
        [location.search, param],
    );
};

type UseSearchParamPath = (
    param: string,
    paramValue?: string | null,
) => [path: string, setSearchParam: (replace?: boolean) => URLSearchParams];

export const useSetSearchParamPath: UseSearchParamPath = (param, paramValue) => {
    const navigate = useNavigate();
    const location = useLocation();
    const createSearchParam = useCreateSearchParam(param);

    const searchParams = useMemo(
        () => createSearchParam(paramValue),
        [createSearchParam, paramValue],
    );

    const path = useMemo(() => {
        if (searchParams.size === 0) {
            return location.pathname;
        }
        return `${location.pathname}?${searchParams.toString()}`;
    }, [location.pathname, searchParams]);

    const setSearchParam = useCallback(
        (replace?: boolean) => {
            void navigate(
                { pathname: location.pathname, search: searchParams.toString() },
                { replace, preventScrollReset: true },
            );
            return searchParams;
        },
        [searchParams, navigate, location.pathname],
    );

    return [path, setSearchParam];
};

export type UseSetSearchParamReturn<T extends string = string> = [
    paramValue: T | null,
    setSearchParam: (paramValue?: string | null, replace?: boolean) => URLSearchParams,
];

export const useSetSearchParams = () => {
    const navigate = useNavigate();
    const { pathname, search } = useLocation();

    return useCallback(
        (updates: Record<string, string | null | undefined>, replace?: boolean) => {
            const searchParams = new URLSearchParams(search);
            for (const [key, value] of Object.entries(updates)) {
                if (value) {
                    searchParams.set(key, value);
                } else {
                    searchParams.delete(key);
                }
            }
            void navigate(
                { pathname, search: searchParams.toString() },
                { replace, preventScrollReset: true },
            );
            return searchParams;
        },
        [navigate, pathname, search],
    );
};

export const useSetSearchParam = <T extends string = string>(
    param: string,
): UseSetSearchParamReturn<T> => {
    const [paramValue] = useGetSearchParam<T>(param);
    const setSearchParams = useSetSearchParams();

    const setSearchParam = useCallback(
        (value?: string | null, replace?: boolean) => {
            return setSearchParams({ [param]: value }, replace);
        },
        [param, setSearchParams],
    );

    return [paramValue, setSearchParam];
};
